import moment from 'moment-timezone';
import { addDays } from 'date-fns';

/**
 * Pure SRS scheduling rules.
 *
 * These decisions used to live inline inside the Firestore-bound fetch and
 * transaction code, which made them impossible to exercise without a database.
 * Keeping them here means the scheduling maths can be tested directly; the
 * Firestore modules stay responsible only for reading and writing.
 */

export const MAX_SRS_STAGE = 7;
export const MIN_REVIEW_STAGE = 1;

/** Sentinel for "no daily cap" in the per-day review settings. */
export const UNLIMITED = -1;

/** Days until the next review, by SRS stage. */
export const srsIntervals = {
  0: 0, // Air
  1: 1, // Wood
  2: 3, // Stone
  3: 7, // Iron
  4: 14, // Gold
  5: 30, // Diamond
  6: 90, // Emerald
  7: 180, // Titanium
};

/**
 * The stage an item moves to after a review.
 *
 * Note the floor is MIN_REVIEW_STAGE, not 0, so a wrong answer never returns an
 * item to the unlearned stage.
 */
export const nextSrsStage = (currentStage, action) =>
  action === 'correct'
    ? Math.min(currentStage + 1, MAX_SRS_STAGE)
    : Math.max(MIN_REVIEW_STAGE, currentStage - 1);

/** When an item at `stage` becomes due, counted from `from`. */
export const nextReviewDateForStage = (stage, from = new Date()) => addDays(from, srsIntervals[stage]);

/**
 * How many reviews the user may still start today.
 *
 * Returns UNLIMITED when no cap is set, otherwise the remaining headroom (never
 * negative). Uses ?? rather than || so an explicit 0 stays a real "none today"
 * cap instead of collapsing into the unlimited sentinel — updateItemReview
 * enforces the setting the same way, and disagreeing would build a queue whose
 * every submission is then rejected.
 */
export const remainingReviewAllowance = (maxPerDay, reviewedToday) => {
  const max = maxPerDay ?? UNLIMITED;
  if (max === UNLIMITED) return UNLIMITED;
  return Math.max(0, max - (reviewedToday || 0));
};

/**
 * The `next_review <= X` cutoff for the review queue.
 *
 * On the 'day' setting everything due by the end of the user's local day is
 * offered; otherwise the window closes at the end of the current UTC hour.
 */
export const reviewDueCutoff = (reviewFrequency, timezone = 'UTC') =>
  reviewFrequency === 'day'
    ? moment().tz(timezone).endOf('day').utc().toDate()
    : moment().utc().endOf('hour').toDate();
